import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";
// const BASE_URL = "http://localhost:8080";

export default function AssessorManagementPage() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "assessors";

    /* ---------------- COMMON ---------------- */
    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );

    /* ---------------- UPLOAD ---------------- */
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select Excel file");
        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        const uploadOneChunk = async (i) => {
            const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const res = await fetch(`${BASE_URL}/api/assessors/upload-json?actor=Rahul`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllAssessors();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    // const downloadTemplate = async () => {
    //     const res = await fetch(`${BASE_URL}/api/assessors/template`, {
    //         headers: authHeaders,
    //     });
    //     const blob = await res.blob();
    //     const url = window.URL.createObjectURL(blob);
    //     const a = document.createElement("a");
    //     a.href = url;
    //     a.download = "assessor_template.xlsx";
    //     a.click();
    //     window.URL.revokeObjectURL(url);
    // };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/assessor_template.xlsx"; // local path
        a.download = "create_assessor_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };
    /* ---------------- TABLE ---------------- */
    const [assessors, setAssessors] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setAssessors(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const scopedUrl = getCenterScopedUrl(BASE_URL, "/api/assessors");
            const res = await fetch(scopedUrl + (scopedUrl.includes("?") ? "&" : "?") + "page=0&size=100", { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setAssessors(all);
            setAllLoaded(false);
            store.setRows(CACHE_KEY, all, false);
        } catch {
            setAssessors([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllAssessors = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/assessors"), { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setAssessors(all);
            setAllLoaded(true);
            setVisibleCount(all.length);
            store.markAllLoaded(CACHE_KEY, all);
        } catch {
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchFirst100();
    }, []);

    useEffect(() => { setVisibleCount(100); }, [search]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return filterRowsForCurrentScope(assessors).filter(
            (a) =>
                a.assessorId?.toLowerCase().includes(q) ||
                a.assessorName?.toLowerCase().includes(q)
        );
    }, [assessors, search]);

    const [exporting, setExporting] = useState(false);

    const exportTableExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = assessors;
            if (!allLoaded) {
                const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/assessors"), { headers: authHeaders });
                const json = await res.json();
                const all = Array.isArray(json) ? json : (json?.content ?? []);
                data = all;
                setAssessors(all);
                setAllLoaded(true);
                setVisibleCount(all.length);
                store.setRows(CACHE_KEY, all, true);
            }
            if (!data.length) return flash("err", "No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Assessor");
            XLSX.writeFile(wb, "assessors.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    /* ---------------- EDIT ---------------- */
    const emptyForm = {

        assessorMobileNumber: "",
        assessorPersonalEmail: "",
        assessorOfficeEmail: "",
        sidhArId: "",
    };

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    // const openEdit = (row) => {
    //     console.log(row);
    //     setEditingId(row.Id);
    //     setForm(row);
    // };

    const openEdit = (row) => {
        console.log("EDIT ROW:", row);
        setEditingId(row.assessorId);   // ✅ FIX HERE
        setForm({
            assessorMobileNumber: row.assessorMobileNumber ?? "",
            assessorPersonalEmail: row.assessorPersonalEmail ?? "",
            assessorOfficeEmail: row.assessorOfficeEmail ?? "",
            sidhArId: row.sidhArId ?? "",
        });

    };


    const updateAssessor = async () => {
        await fetch(
            `${BASE_URL}/api/assessors?assessorId=${encodeURIComponent(editingId)}`,
            {
                method: "PUT",
                headers: jsonHeaders,
                body: JSON.stringify(form),
            }
        );
        flash("ok", "Assessor updated");
        setEditingId(null);
        store.invalidate(CACHE_KEY);
        fetchAllAssessors();
    };

    const deleteAssessor = async (id) => {
        if (!window.confirm("Deactivate this assessor?")) return;

        await fetch(
            `${BASE_URL}/api/assessors?assessorId=${encodeURIComponent(id)}`,
            {
                method: "DELETE",
                headers: authHeaders,
            }
        );
        flash("ok", "Assessor deactivated");
        store.invalidate(CACHE_KEY);
        fetchAllAssessors();
    };

    return (
        <div className="cmp-page">
            <div className="cmp-header">
                <div style={{ marginBottom: "1rem" }}>
                    <h1>Assessor Management</h1>
                    {/* <p>Excel upload, update, edit and export assessors</p> */}
                </div>
            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* Upload */}
            <div className="card">
                {/* <div className="card-title">1️⃣ Excel Upload / Update</div> */}
                <div className="grid-3">

                    <input
                        style={{ border: "1px solid rgb(229, 231, 235)", borderRadius: "10px", padding: "10px 12px", background: "rgb(255, 255, 255);" }}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setFile(e.target.files[0])}
                    />
                    <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                        {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                    </button>
                    <button className="btn" onClick={downloadTemplate}>
                        Download Template
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="card">
                <div className="card-top">
                    <div className="card-title">2️⃣ Assessors Table</div>
                    <div className="table-actions">
                        <input
                            className="search"
                            placeholder="Search assessor..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button className="btn" onClick={exportTableExcel} disabled={exporting}>
                            {exporting ? "Processing..." : "Export Excel"}
                        </button>
                    </div>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>Assessor ID</th>
                            <th>Mobile</th>
                            <th>Personal Email</th>
                            <th>Office Email</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.slice(0, visibleCount).map((t) => (
                            <tr key={t.assessorId}>
                                <td>{t.assessorId}</td>
                                <td>{t.assessorMobileNumber}</td>
                                <td>{t.assessorPersonalEmail}</td>
                                <td>{t.assessorOfficeEmail}</td>
                                <td className="actions">
                                    {/* <button className="btn btn-sm" onClick={() => openEdit(t)}>
                                        Edit
                                    </button> */}
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => deleteAssessor(t.assessorId)}
                                    >
                                        Deactivate
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!filtered.length && (
                            <tr>
                                <td colSpan="6" className="muted">
                                    {loading ? "Loading..." : "No assessors found"}
                                </td>
                            </tr>
                        )}
                        {!allLoaded && !loading && (
                            <tr>
                                <td colSpan="6" style={{ textAlign: "center", padding: "14px" }}>
                                    <button className="btn btn-outline" onClick={fetchAllAssessors} disabled={loadingAll}>
                                        {loadingAll ? "Loading all data…" : "Load All Data"}
                                    </button>
                                </td>
                            </tr>
                        )}
                        {allLoaded && filtered.length > visibleCount && (
                            <tr>
                                <td colSpan="6" style={{ textAlign: "center", padding: "14px" }}>
                                    <button
                                        className="btn btn-outline"
                                        onClick={() => setVisibleCount((c) => c + 100)}
                                    >
                                        Show More ({filtered.length - visibleCount} remaining)
                                    </button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* EDIT MODAL */}
            {editingId &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setEditingId(null)}>
                        <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-title">Edit Assessor</div>

                            {Object.keys(emptyForm).map((k) => (
                                <div className="field" key={k}>
                                    <label>{k}</label>
                                    <input
                                        value={form[k] || ""}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, [k]: e.target.value }))
                                        }
                                    />
                                </div>
                            ))}

                            <div className="modal-actions">
                                <button className="btn" onClick={() => setEditingId(null)}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary" onClick={updateAssessor}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {/* INLINE CSS */}
            <style>{`
        .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
        .cmp-header h1 { margin:0; font-size:26px; font-weight:900 }
        .cmp-header p { color:#6b7280; font-size:13px }

        .flash { padding:10px; border-radius:10px; margin:12px 0; font-weight:700 }
        .flash-ok { background:#ecfdf5; color:#065f46 }
        .flash-err { background:#fef2f2; color:#991b1b }

        .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
        .card-title { font-weight:900; margin-bottom:10px }

        .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

        .btn { border:1px solid #e5e7eb; background:#fff; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer }
        .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
        .btn-sm { padding:6px 10px; font-size:12px }
        .btn-danger { border-color:#fca5a5; color:#991b1b }

        table { width:100%; border-collapse:collapse }
        th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px }
        th { color:#6b7280; text-align:left }

        .table-actions { display:flex; gap:10px; align-items:center }
        .search { padding:10px; border-radius:12px; border:1px solid #e5e7eb }

        .actions { display:flex; gap:8px }
        .muted { color:#6b7280; font-weight:700 }

        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
        .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
        .modal-title { font-size:18px; font-weight:900; margin-bottom:12px }

        .field { display:flex; flex-direction:column; gap:6px; margin-bottom:10px }
        .field label { font-size:12px; color:#6b7280; font-weight:800 }
        .field input { padding:10px; border-radius:12px; border:1px solid #e5e7eb }

        .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
      `}</style>
        </div>
    );
}
